import React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Post as PostOrganism } from 'organisms';
import { Post as PostType } from 'types/post';
import { reportError } from 'lib/errors';
import { LoadingError } from 'atoms';
import type { ErrorLike } from '@apollo/client';
import { useRouter } from 'next/router';
import useStyles from './styles';
import { PageProps } from 'types/page';
import { DOMAIN_BASE_SHORT, ROUTES } from 'config/Nav';
import PopularPostList from 'organisms/PopularPostList';
import { Button, Seo, primaryOutlinedButtonSx } from '@elevator/ui';

/**
 * Maps post data into the `Post` organism. Used by the full post page and by preview.
 *
 * Used in:
 * - This file (success path of the default post layout)
 * - `pages/preview/[slug].tsx`
 */
export const PostArticle = (props: PostPropTypes): React.JSX.Element => {
  const { post, error, hasInstagramEmbed, hasTwitterEmbed, isLoading } = props;

  if (error) {
    return <LoadingError error={error} failedTarget={'post'} reportError={reportError} />;
  }

  if (!post) {
    return (
      <LoadingError
        error={new Error('Post data is missing')}
        failedTarget={'post'}
        reportError={reportError}
      />
    );
  }

  const {
    title,
    slug,
    type,
    description,
    content,
    publishedAt,
    authors,
    categories,
    tags,
    submission,
    credits,
    modifiedAt,
  } = post;

  const postHeaderProps = {
    title,
    description,
    slug,
    type,
    publishedAt,
    authors,
    categories,
    modifiedAt,
  };

  return (
    <PostOrganism
      content={content}
      submission={submission}
      tags={tags}
      credits={credits}
      hasInstagramEmbed={hasInstagramEmbed}
      hasTwitterEmbed={hasTwitterEmbed}
      isLoading={isLoading}
      {...postHeaderProps}
    />
  );
};

/**
 * Post layout: SEO, error / 404 handling, article body, trending posts.
 *
 * Features:
 * - `data-layout="post"` on the layout root for each branch
 * - Article SEO metadata
 * - Success path: `<main>` wraps `PostArticle`; `<aside>` wraps trending `PopularPostList`
 * - Popular posts below on success
 *
 * Used in:
 * - `layouts/Post/index.tsx` (with `withLayout`)
 */
const Post = (props: PostIndexProps): React.JSX.Element => {
  const { classes } = useStyles();
  const router = useRouter();

  const { post, slug, error, hasInstagramEmbed, hasTwitterEmbed, isLoading } = props;

  if (error) {
    return (
      <Box className={classes.errorLoadingDisclaimer} data-layout="post">
        <LoadingError error={error} failedTarget={'post'} reportError={reportError} />
      </Box>
    );
  }

  if (!post) {
    return (
      <Box className={classes.errorRootContainer} data-layout="post">
        <Stack spacing={2} alignItems="center" width="100%">
          <Typography align="center" variant="h4" component="h1" gutterBottom>
            404 post not found
          </Typography>
          <Typography
            align="center"
            variant="body1"
            sx={{ fontSize: (theme) => theme.typography.pxToRem(18) }}
          >
            We had trouble finding that post
          </Typography>
          <Stack pt={4} alignItems="center">
            <Button
              variant="outlined"
              sx={primaryOutlinedButtonSx}
              onClick={() => router.push(ROUTES.HOME.to)}
            >
              Take me home
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  }

  const authors = post.authors.map((author) => author.preferredUsername);

  return (
    <Box className={classes.container} data-layout="post">
      <Seo
        type="article"
        data={{
          baseUrl: DOMAIN_BASE_SHORT,
          title: post.title,
          description: `${post.description} -- by ${authors.join(', ')}`,
          path: `/${slug}`,
          image: {
            url: post.image.sourceUrl,
            alt: post.description,
          },
          article: {
            publishedTime: post.publishedAt?.toString() || '',
            modifiedTime: post.modifiedAt,
            authors,
            tags: post.tags.map((tag) => tag.name),
          },
        }}
      />
      <Box component="main" className={classes.postMain} width="100%">
        <PostArticle
          post={post}
          slug={slug}
          error={error}
          hasInstagramEmbed={hasInstagramEmbed}
          hasTwitterEmbed={hasTwitterEmbed}
          isLoading={isLoading}
        />
      </Box>
      <Box component="aside" className={classes.postAside} width="100%">
        <PopularPostList variant="main" />
      </Box>
    </Box>
  );
};

export interface PostPropTypes {
  post?: PostType;
  slug: string;
  error?: ErrorLike | null;
  hasInstagramEmbed: boolean;
  hasTwitterEmbed: boolean;
  isLoading: boolean;
}

export interface PostIndexProps extends PageProps {
  post?: PostType;
  slug: string;
  error?: ErrorLike | null;
  hasInstagramEmbed?: boolean;
  hasTwitterEmbed?: boolean;
  isLoading: boolean;
}

export default Post;
